import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatCentsAsCurrency } from '../utils/formatting';
import { formatCurrency, toCents, invoiceBalance, calculateTaxBreakdown, calculateTotal } from '../utils/finance';
import { paginate, pageWindow } from '../utils/pagination';
import { getStatusBadgeColor, getPaymentStatusBadgeColor } from '../utils/invoiceStatus';
import { resolveOrganizationName } from '../utils/organizations';
import {
  invoicesApi,
  tenantsApi,
  organizationsApi,
  taxRatesApi,
  paymentsApi,
  paymentMethodsApi,
  settlementImportApi,
  Invoice,
  Tenant,
  Organization,
  TaxRate,
  Payment,
  PaymentMethod,
  BankFormat,
  ImportResult,
  CreateInvoiceDto,
  BillingInfo,
  ContactInfo,
  LineItem,
} from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';

type CreateStep = 1 | 2 | 3 | 4;

export const Invoices: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  // Pre-fill the tenant filter from the ?tenantId= query param
  const [filterTenantId, setFilterTenantId] = useState<string>(
    () => searchParams.get('tenantId') ?? ''
  );
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Add Payment form state
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [addPaymentFormData, setAddPaymentFormData] = useState({
    payment_method_id: '',
    amount: '',
    transaction_id: '',
    notes: '',
    markAsCompleted: false,
  });
  const [addPaymentFormError, setAddPaymentFormError] = useState('');
  const [addPaymentFormLoading, setAddPaymentFormLoading] = useState(false);
  const [tenantPaymentMethods, setTenantPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);

  // Multi-step form state
  const [createStep, setCreateStep] = useState<CreateStep>(1);
  const [formData, setFormData] = useState<CreateInvoiceDto>({
    tenant_id: '',
    currency: 'USD',
    amount: 0,
    billing_info: {
      contact_name: '',
      address_line1: '',
      city: '',
      postal_code: '',
      country: '',
    },
    contact_info: {
      email: '',
    },
    line_items: [
      {
        description: '',
        quantity: 1,
        unit_price: 0,
        amount: 0,
      },
    ],
    tax_rate_id: undefined,
    prices_include_tax: true,
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Import settlements state
  const [showImportModal, setShowImportModal] = useState(false);
  const [bankFormats, setBankFormats] = useState<BankFormat[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedBankFormat, setSelectedBankFormat] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [importTenantId, setImportTenantId] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    fetchInvoices();
    fetchTenants();
    fetchTaxRates();
  }, [token, filterTenantId, filterStatus]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTenantId, filterStatus]);

  // Pagination calculations
  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInvoices = paginate(invoices, currentPage, itemsPerPage);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const fetchInvoices = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await invoicesApi.getAll(token, filterTenantId || undefined);

      // Apply status filter on client side
      const filtered = filterStatus
        ? data.filter((inv) => inv.status === filterStatus)
        : data;

      setInvoices(filtered);
      setError('');
    } catch (err) {
      setError(t('invoices.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    if (!token) return;

    try {
      const [tenantsData, orgsData] = await Promise.all([
        tenantsApi.getAll(token),
        organizationsApi.getAll(token),
      ]);
      setTenants(tenantsData.filter((t) => t.is_active));
      setOrganizations(orgsData);
    } catch (err) {
      console.error('Failed to load tenants');
    }
  };

  const fetchTaxRates = async () => {
    if (!token) return;

    try {
      const data = await taxRatesApi.getActive(token);
      setTaxRates(data);
    } catch (err) {
      console.error('Failed to load tax rates');
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      currency: 'USD',
      amount: 0,
      billing_info: {
        contact_name: '',
        address_line1: '',
        city: '',
        postal_code: '',
        country: '',
      },
      contact_info: {
        email: '',
      },
      line_items: [
        {
          description: '',
          quantity: 1,
          unit_price: 0,
          amount: 0,
        },
      ],
      tax_rate_id: undefined,
      prices_include_tax: true,
    });
    setCreateStep(1);
    setFormError('');
  };

  const handleCreateInvoice = async (finalAmount: number) => {
    if (!token) return;

    setFormLoading(true);
    setFormError('');

    try {
      // Create invoice DTO with calculated final amount
      const invoiceDto = {
        ...formData,
        amount: finalAmount,
      };

      const created = await invoicesApi.create(token, invoiceDto);
      setShowCreateModal(false);
      resetForm();
      await fetchInvoices();

      // Show settlement URL immediately after creation
      setSelectedInvoice(created);
      setShowSettlementModal(true);
    } catch (err: any) {
      setFormError(err.message || 'Failed to create invoice');
    } finally {
      setFormLoading(false);
    }
  };

  const handleInvalidate = async (invoice: Invoice) => {
    if (!token || !confirm('Are you sure you want to invalidate this invoice?')) return;

    try {
      await invoicesApi.invalidate(token, invoice.invoice_id);
      await fetchInvoices();
    } catch (err: any) {
      alert(err.message || 'Failed to invalidate invoice');
    }
  };

  const handleResendEmail = async (invoice: Invoice) => {
    if (!token) return;

    try {
      const result = await invoicesApi.resendEmail(token, invoice.invoice_id);
      alert(result.message || 'Invoice email has been sent');
    } catch (err: any) {
      alert(err.message || 'Failed to resend invoice email');
    }
  };

  const handleCreateTestInvoice = async () => {
    if (!token) return;

    if (tenants.length === 0) {
      alert('No tenants available. Please create a tenant first.');
      return;
    }

    try {
      setFormLoading(true);
      setFormError('');

      // Create test invoice data
      const testInvoice: CreateInvoiceDto = {
        tenant_id: tenants[0].tenant_id,
        currency: 'EUR',
        amount: 20000, // Will be recalculated with tax
        billing_info: {
          contact_name: 'Test Customer',
          company_name: 'Test Company GmbH',
          address_line1: 'Teststraße 123',
          city: 'Berlin',
          postal_code: '10115',
          country: 'Germany',
        },
        contact_info: {
          email: 'test@example.com',
          phone: '+49 30 12345678',
        },
        line_items: [
          {
            description: 'Test Item 1 - Premium Service',
            quantity: 2,
            unit_price: 5000, // 50 EUR
            amount: 10000, // 100 EUR
          },
          {
            description: 'Test Item 2 - Consultation',
            quantity: 1,
            unit_price: 10000, // 100 EUR
            amount: 10000, // 100 EUR
          },
        ],
        tax_rate_id: taxRates.length > 0 ? taxRates[0].tax_rate_id : undefined,
        prices_include_tax: true,
      };

      const created = await invoicesApi.create(token, testInvoice);
      await fetchInvoices();

      // Show settlement URL
      setSelectedInvoice(created);
      setShowSettlementModal(true);
    } catch (err: any) {
      alert(err.message || 'Failed to create test invoice');
    } finally {
      setFormLoading(false);
    }
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        { description: '', quantity: 1, unit_price: 0, amount: 0 },
      ],
    });
  };

  const removeLineItem = (index: number) => {
    if (formData.line_items.length === 1) return; // Keep at least one
    setFormData({
      ...formData,
      line_items: formData.line_items.filter((_, i) => i !== index),
    });
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };

    // Auto-calculate amount
    if (field === 'quantity' || field === 'unit_price') {
      newLineItems[index].amount =
        newLineItems[index].quantity * newLineItems[index].unit_price;
    }

    setFormData({ ...formData, line_items: newLineItems });
  };

  // Tax preview for the create form. calculateTaxBreakdown applies the rate per
  // line item with Math.round, matching the backend's persisted amounts
  // (fixed DEFECT-28).
  const selectedTaxRate = taxRates.find((r) => r.tax_rate_id === formData.tax_rate_id);
  const lineItemsTotal = calculateTotal(formData.line_items);
  const taxBreakdown = calculateTaxBreakdown(
    formData.line_items,
    formData.prices_include_tax,
    selectedTaxRate?.rate_percentage
  );

  const nextStep = () => {
    if (createStep < 4) setCreateStep((createStep + 1) as CreateStep);
  };

  const prevStep = () => {
    if (createStep > 1) setCreateStep((createStep - 1) as CreateStep);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const fetchPayments = async (invoiceId: string) => {
    if (!token) return;
    try {
      setPaymentsLoading(true);
      const data = await paymentsApi.getAll(token, invoiceId);
      setPayments(data);
    } catch (err) {
      console.error('Failed to load payments', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchTenantPaymentMethods = async (tenantId: string) => {
    if (!token) return;
    setPaymentMethodsLoading(true);
    try {
      const methods = await paymentMethodsApi.getByTenant(token, tenantId);
      setTenantPaymentMethods(methods.filter((pm) => pm.is_active));
    } catch (err) {
      console.error('Failed to load payment methods', err);
      setTenantPaymentMethods([]);
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const resetAddPaymentForm = () => {
    setAddPaymentFormData({
      payment_method_id: '',
      amount: '',
      transaction_id: '',
      notes: '',
      markAsCompleted: false,
    });
    setAddPaymentFormError('');
    setShowAddPaymentForm(false);
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedInvoice) return;

    const amountFloat = parseFloat(addPaymentFormData.amount);
    if (isNaN(amountFloat) || amountFloat <= 0) {
      setAddPaymentFormError('Please enter a valid amount greater than 0');
      return;
    }
    if (!addPaymentFormData.payment_method_id) {
      setAddPaymentFormError('Please select a payment method');
      return;
    }

    setAddPaymentFormLoading(true);
    setAddPaymentFormError('');

    try {
      // Create payment (starts as 'pending')
      const payment = await paymentsApi.create(token, {
        invoice_id: selectedInvoice.invoice_id,
        payment_method_id: addPaymentFormData.payment_method_id,
        amount: toCents(amountFloat), // Convert to cents
        transaction_id: addPaymentFormData.transaction_id || undefined,
        notes: addPaymentFormData.notes || undefined,
      });

      // If markAsCompleted is checked, update status to 'completed'
      if (addPaymentFormData.markAsCompleted) {
        await paymentsApi.updateStatus(token, payment.payment_id, { status: 'completed' });
      }

      await fetchPayments(selectedInvoice.invoice_id);
      await fetchInvoices();
      resetAddPaymentForm();
    } catch (err: any) {
      setAddPaymentFormError(err.message || 'Failed to create payment');
    } finally {
      setAddPaymentFormLoading(false);
    }
  };

  const handleUpdatePaymentStatus = async (paymentId: string, newStatus: string) => {
    if (!token || !selectedInvoice) return;
    try {
      await paymentsApi.updateStatus(token, paymentId, { status: newStatus as any });
      await fetchPayments(selectedInvoice.invoice_id);
      await fetchInvoices(); // Refresh invoice list to update totals
    } catch (err: any) {
      alert(err.message || 'Failed to update payment status');
    }
  };

  // Import settlements functions
  const fetchBankFormats = async () => {
    if (!token) return;
    try {
      const formats = await settlementImportApi.getBankFormats(token);
      setBankFormats(formats);
    } catch (err) {
      console.error('Failed to load bank formats', err);
    }
  };

  const fetchPaymentMethodsForTenant = async (tenantId: string) => {
    if (!token || !tenantId) {
      setPaymentMethods([]);
      return;
    }
    try {
      const methods = await paymentMethodsApi.getByTenant(token, tenantId);
      // Only show payment methods with a reference_prefix configured in settings
      setPaymentMethods(methods.filter((pm) => pm.settings?.reference_prefix && pm.is_active));
    } catch (err) {
      console.error('Failed to load payment methods', err);
    }
  };

  const resetImportForm = () => {
    setSelectedBankFormat('');
    setSelectedPaymentMethod('');
    setImportTenantId('');
    setImportFile(null);
    setImportResult(null);
    setImportError('');
    setPaymentMethods([]);
  };

  const handleImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !importTenantId || !selectedPaymentMethod || !selectedBankFormat || !importFile) {
      setImportError('Please fill in all required fields');
      return;
    }

    setImportLoading(true);
    setImportError('');

    try {
      const result = await settlementImportApi.importSettlements(
        token,
        importTenantId,
        selectedPaymentMethod,
        selectedBankFormat,
        importFile,
      );
      setImportResult(result);
      // Refresh invoices to show updated payment status
      await fetchInvoices();
    } catch (err: any) {
      setImportError(err.message || 'Failed to import settlements');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <AdminLayout title={t('invoices.title')} subtitle={t('invoices.subtitle')}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="space-x-2">
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="bg-[var(--navy)] text-white px-4 py-2 rounded-lg hover:bg-[var(--navy-light)]"
            >
              {t('invoices.createInvoice')}
            </button>
            <button
              onClick={handleCreateTestInvoice}
              disabled={formLoading}
              className="text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              style={{ background: 'var(--success)' }}
            >
              {formLoading ? t('common.creating') : t('invoices.createTestInvoice')}
            </button>
            <button
              onClick={() => {
                resetImportForm();
                fetchBankFormats();
                setShowImportModal(true);
              }}
              className="bg-[var(--navy)] text-white px-4 py-2 rounded-lg hover:bg-[var(--navy-light)]"
            >
              {t('invoices.importSettlements')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.filterByTenant')}
              </label>
              <select
                value={filterTenantId}
                onChange={(e) => setFilterTenantId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">{t('invoices.allTenants')}</option>
                {tenants.map((tenant) => (
                  <option key={tenant.tenant_id} value={tenant.tenant_id}>
                    {resolveOrganizationName(organizations, tenant.organization_id)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.filterByStatus')}
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">{t('invoices.allStatuses')}</option>
                <option value="draft">{t('invoices.draft')}</option>
                <option value="pending">{t('common.pending')}</option>
                <option value="paid">{t('invoices.paid')}</option>
                <option value="partially_paid">{t('invoices.partiallyPaid')}</option>
                <option value="cancelled">{t('invoices.cancelled')}</option>
                <option value="invalidated">{t('invoices.invalidated')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Invoices Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('invoices.loadingInvoices')}</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500">{t('invoices.noInvoices')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('invoices.invoiceNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('invoices.tenant')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('invoices.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('invoices.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('invoices.createdAt')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedInvoices.map((invoice) => (
                  <tr key={invoice.invoice_id} className="hover:bg-[var(--gold-pale)]">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invoice.invoice_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.tenant?.organization_id
                        ? resolveOrganizationName(organizations, invoice.tenant.organization_id)
                        : invoice.tenant_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          fetchPayments(invoice.invoice_id);
                          setShowPaymentsModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        {t('invoices.payments', { count: invoice.payment_count || 0 })}
                      </button>
                      {(invoice.status === 'pending' || invoice.status === 'partially_paid') && (
                        <button
                          onClick={() => handleResendEmail(invoice)}
                          className="text-green-600 hover:text-green-900"
                        >
                          {t('invoices.resendEmail')}
                        </button>
                      )}
                      {(invoice.status === 'pending' || invoice.status === 'partially_paid') && (
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowSettlementModal(true);
                          }}
                          className="text-[var(--navy)] hover:text-[var(--navy-light)]"
                        >
                          {t('invoices.settlementLink')}
                        </button>
                      )}
                      {invoice.status === 'pending' && (
                        <button
                          onClick={() => handleInvalidate(invoice)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('invoices.invalidate')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-[var(--gold-pale)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-[var(--gold-pale)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      {t('common.showing')} <span className="font-medium">{startIndex + 1}</span> -{' '}
                      <span className="font-medium">{Math.min(endIndex, invoices.length)}</span>{' '}
                      {t('common.of')} <span className="font-medium">{invoices.length}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-[var(--gold-pale)] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">{t('common.previous')}</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {pageWindow(totalPages, currentPage).map((page, index) => (
                        <React.Fragment key={`${page}-${index}`}>
                          {page === 'ellipsis' && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          )}
                          {page !== 'ellipsis' && (
                            <button
                              onClick={() => goToPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-[var(--gold-pale)] border-[var(--gold)] text-[var(--navy)]'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-[var(--gold-pale)]'
                              }`}
                            >
                              {page}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-[var(--gold-pale)] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">{t('common.next')}</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Invoice Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Create Invoice - Step {createStep} of 4
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center mb-6">
                  {[1, 2, 3, 4].map((step) => (
                    <React.Fragment key={step}>
                      <div
                        className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full ${
                          step <= createStep
                            ? 'bg-[var(--navy)] text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {step}
                      </div>
                      {step < 4 && (
                        <div
                          className={`flex-1 h-1 mx-2 ${
                            step < createStep ? 'bg-[var(--navy)]' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {formError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {formError}
                  </div>
                )}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (createStep === 4) {
                      // Calculate total amount from line items (including tax if applicable)
                      handleCreateInvoice(taxBreakdown.total);
                    } else {
                      nextStep();
                    }
                  }}
                >
                  {/* Step 1: Basic Info */}
                  {createStep === 1 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tenant *
                        </label>
                        <select
                          required
                          value={formData.tenant_id}
                          onChange={(e) =>
                            setFormData({ ...formData, tenant_id: e.target.value })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="">Select a tenant</option>
                          {tenants.map((tenant) => (
                            <option key={tenant.tenant_id} value={tenant.tenant_id}>
                              {resolveOrganizationName(organizations, tenant.organization_id)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currency *
                        </label>
                        <select
                          required
                          value={formData.currency}
                          onChange={(e) =>
                            setFormData({ ...formData, currency: e.target.value })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                          <option value="GBP">GBP - British Pound</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tax Rate
                        </label>
                        <select
                          value={formData.tax_rate_id || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, tax_rate_id: e.target.value || undefined })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="">No Tax</option>
                          {taxRates.map((rate) => (
                            <option key={rate.tax_rate_id} value={rate.tax_rate_id}>
                              {rate.tax_rate_name} ({rate.rate_percentage}%)
                            </option>
                          ))}
                        </select>
                      </div>
                      {formData.tax_rate_id && (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="prices_include_tax"
                            checked={formData.prices_include_tax}
                            onChange={(e) =>
                              setFormData({ ...formData, prices_include_tax: e.target.checked })
                            }
                            className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
                          />
                          <label htmlFor="prices_include_tax" className="ml-2 text-sm text-gray-700">
                            Prices include tax (post-tax)
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Billing Info */}
                  {createStep === 2 && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company Name
                          </label>
                          <input
                            type="text"
                            value={formData.billing_info.company_name || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_info: {
                                  ...formData.billing_info,
                                  company_name: e.target.value,
                                },
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.billing_info.contact_name}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_info: {
                                  ...formData.billing_info,
                                  contact_name: e.target.value,
                                },
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address Line 1 *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.billing_info.address_line1}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              billing_info: {
                                ...formData.billing_info,
                                address_line1: e.target.value,
                              },
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          value={formData.billing_info.address_line2 || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              billing_info: {
                                ...formData.billing_info,
                                address_line2: e.target.value,
                              },
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.billing_info.city}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_info: {
                                  ...formData.billing_info,
                                  city: e.target.value,
                                },
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State/Province
                          </label>
                          <input
                            type="text"
                            value={formData.billing_info.state || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_info: {
                                  ...formData.billing_info,
                                  state: e.target.value,
                                },
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Postal Code *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.billing_info.postal_code}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_info: {
                                  ...formData.billing_info,
                                  postal_code: e.target.value,
                                },
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.billing_info.country}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_info: {
                                  ...formData.billing_info,
                                  country: e.target.value,
                                },
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tax ID
                        </label>
                        <input
                          type="text"
                          value={formData.billing_info.tax_id || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              billing_info: {
                                ...formData.billing_info,
                                tax_id: e.target.value,
                              },
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Contact Info */}
                  {createStep === 3 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.contact_info.email}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contact_info: {
                                ...formData.contact_info,
                                email: e.target.value,
                              },
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={formData.contact_info.phone || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contact_info: {
                                ...formData.contact_info,
                                phone: e.target.value,
                              },
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 4: Line Items */}
                  {createStep === 4 && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium">Line Items</h3>
                        <button
                          type="button"
                          onClick={addLineItem}
                          className="text-sm text-[var(--navy)] hover:text-[var(--navy-light)]"
                        >
                          + Add Item
                        </button>
                      </div>

                      {formData.line_items.map((item, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4 space-y-3"
                        >
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium text-gray-700">
                              Item {index + 1}
                            </span>
                            {formData.line_items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm text-gray-700 mb-1">
                              Description *
                            </label>
                            <input
                              type="text"
                              required
                              value={item.description}
                              onChange={(e) =>
                                updateLineItem(index, 'description', e.target.value)
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-sm text-gray-700 mb-1">
                                Quantity *
                              </label>
                              <input
                                type="number"
                                required
                                min="1"
                                value={item.quantity}
                                onChange={(e) =>
                                  updateLineItem(
                                    index,
                                    'quantity',
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-700 mb-1">
                                Unit Price (cents) *
                              </label>
                              <input
                                type="number"
                                required
                                min="0"
                                value={item.unit_price}
                                onChange={(e) =>
                                  updateLineItem(
                                    index,
                                    'unit_price',
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-700 mb-1">
                                Amount
                              </label>
                              <input
                                type="number"
                                readOnly
                                value={item.amount}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50"
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="border-t border-gray-200 pt-4 space-y-2">
                        {formData.tax_rate_id ? (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">
                                {formData.prices_include_tax ? 'Subtotal (excl. tax):' : 'Subtotal:'}
                              </span>
                              <span className="text-gray-900">
                                {formatCurrency(taxBreakdown.subtotal, formData.currency)}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">
                                Tax ({taxRates.find((r) => r.tax_rate_id === formData.tax_rate_id)?.rate_percentage}%):
                              </span>
                              <span className="text-gray-900">
                                {formatCurrency(taxBreakdown.taxAmount, formData.currency)}
                              </span>
                            </div>
                            <div className="flex justify-between text-lg font-bold border-t pt-2">
                              <span>Total:</span>
                              <span>{formatCurrency(taxBreakdown.total, formData.currency)}</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total:</span>
                            <span>{formatCurrency(lineItemsTotal, formData.currency)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex justify-between mt-6">
                    <div>
                      {createStep > 1 && (
                        <button
                          type="button"
                          onClick={prevStep}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-[var(--gold-pale)]"
                        >
                          Previous
                        </button>
                      )}
                    </div>
                    <div className="space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateModal(false);
                          resetForm();
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-[var(--gold-pale)]"
                      >
                        Cancel
                      </button>
                      {createStep < 4 ? (
                        <button
                          type="submit"
                          className="px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)]"
                        >
                          Next
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={formLoading}
                          className="px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)] disabled:opacity-50"
                        >
                          {formLoading ? 'Creating...' : 'Create Invoice'}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Settlement URL Modal */}
        {showSettlementModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Settlement Link
                </h2>
                <button
                  onClick={() => {
                    setShowSettlementModal(false);
                    setSelectedInvoice(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-2">
                    Invoice: <span className="font-medium text-gray-900">{selectedInvoice.invoice_number}</span>
                  </p>
                  <p className="text-sm text-gray-500 mb-2">
                    Amount: <span className="font-medium text-gray-900">
                      {formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}
                    </span>
                  </p>
                </div>

                {selectedInvoice.settlement_url && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Share this link with the payer:
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={selectedInvoice.settlement_url}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50"
                      />
                      <button
                        onClick={() => copyToClipboard(selectedInvoice.settlement_url!)}
                        className="px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)]"
                      >
                        Copy
                      </button>
                      <a
                        href={selectedInvoice.settlement_url!}
                        target="_blank"
                        className="px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)]"
                      >
                        Open
                      </a>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      This link contains a security token and allows the payer to view
                      and settle the invoice.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowSettlementModal(false);
                    setSelectedInvoice(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payments Modal */}
        {showPaymentsModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Payments for {selectedInvoice.invoice_number}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Total: {formatCurrency(selectedInvoice.amount, selectedInvoice.currency)} |
                      Paid: {formatCurrency(selectedInvoice.total_paid, selectedInvoice.currency)} |
                      Balance: {formatCurrency(
                        invoiceBalance(selectedInvoice),
                        selectedInvoice.currency
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedInvoice.status !== 'cancelled' &&
                     selectedInvoice.status !== 'invalidated' &&
                     selectedInvoice.status !== 'paid' && (
                      <button
                        onClick={() => {
                          fetchTenantPaymentMethods(selectedInvoice.tenant_id);
                          setShowAddPaymentForm(true);
                        }}
                        className="text-white px-3 py-1.5 rounded-lg text-sm hover:bg-emerald-700"
                        style={{ background: 'var(--success)' }}
                      >
                        + Add Payment
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowPaymentsModal(false);
                        setSelectedInvoice(null);
                        setPayments([]);
                        resetAddPaymentForm();
                        setTenantPaymentMethods([]);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Add Payment Form */}
                {showAddPaymentForm && (
                  <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Add Manual Payment</h3>
                      <button
                        onClick={resetAddPaymentForm}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >
                        Cancel
                      </button>
                    </div>

                    {addPaymentFormError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {addPaymentFormError}
                      </div>
                    )}

                    <form onSubmit={handleAddPayment}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Payment Method */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Method *
                          </label>
                          {paymentMethodsLoading ? (
                            <div className="text-sm text-gray-500">Loading payment methods...</div>
                          ) : tenantPaymentMethods.length === 0 ? (
                            <div className="text-sm text-yellow-600">
                              No active payment methods for this tenant.
                            </div>
                          ) : (
                            <select
                              required
                              value={addPaymentFormData.payment_method_id}
                              onChange={(e) =>
                                setAddPaymentFormData({
                                  ...addPaymentFormData,
                                  payment_method_id: e.target.value,
                                })
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            >
                              <option value="">Select payment method</option>
                              {tenantPaymentMethods.map((pm) => (
                                <option key={pm.payment_method_id} value={pm.payment_method_id}>
                                  {pm.display_name || pm.payment_method_name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        {/* Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount ({selectedInvoice.currency}) *
                          </label>
                          <input
                            type="number"
                            required
                            min="0.01"
                            step="0.01"
                            placeholder="0.00"
                            value={addPaymentFormData.amount}
                            onChange={(e) =>
                              setAddPaymentFormData({
                                ...addPaymentFormData,
                                amount: e.target.value,
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Remaining balance: {formatCurrency(
                              invoiceBalance(selectedInvoice),
                              selectedInvoice.currency
                            )}
                          </p>
                        </div>

                        {/* Transaction ID */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Transaction ID (optional)
                          </label>
                          <input
                            type="text"
                            placeholder="External reference number"
                            value={addPaymentFormData.transaction_id}
                            onChange={(e) =>
                              setAddPaymentFormData({
                                ...addPaymentFormData,
                                transaction_id: e.target.value,
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notes (optional)
                          </label>
                          <input
                            type="text"
                            placeholder="Additional information"
                            value={addPaymentFormData.notes}
                            onChange={(e) =>
                              setAddPaymentFormData({
                                ...addPaymentFormData,
                                notes: e.target.value,
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      {/* Mark as Completed checkbox */}
                      <div className="mt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={addPaymentFormData.markAsCompleted}
                            onChange={(e) =>
                              setAddPaymentFormData({
                                ...addPaymentFormData,
                                markAsCompleted: e.target.checked,
                              })
                            }
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700">
                            Mark as completed (payment already confirmed)
                          </span>
                        </label>
                      </div>

                      {/* Submit Button */}
                      <div className="mt-4 flex justify-end">
                        <button
                          type="submit"
                          disabled={addPaymentFormLoading || tenantPaymentMethods.length === 0}
                          className="text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                          style={{ background: 'var(--success)' }}
                        >
                          {addPaymentFormLoading ? 'Creating...' : 'Create Payment'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Payments List */}
                <div className="mb-4">
                  {paymentsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="mt-2 text-gray-500">Loading payments...</p>
                    </div>
                  ) : payments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No payments recorded yet
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Method</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Transaction ID</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {payments.map((payment) => (
                          <tr key={payment.payment_id}>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(payment.paid_at ?? payment.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {formatCurrency(payment.amount, selectedInvoice.currency)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {payment.payment_method?.display_name || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadgeColor(payment.status)}`}>
                                {payment.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {payment.transaction_id || '-'}
                            </td>
                            <td className="px-4 py-3 text-right text-sm">
                              {payment.status === 'pending' && (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleUpdatePaymentStatus(payment.payment_id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                  defaultValue=""
                                >
                                  <option value="" disabled>Change status...</option>
                                  <option value="completed">Mark as Completed</option>
                                  <option value="failed">Mark as Failed</option>
                                </select>
                              )}
                              {payment.status === 'completed' && (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleUpdatePaymentStatus(payment.payment_id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                  defaultValue=""
                                >
                                  <option value="" disabled>Change status...</option>
                                  <option value="refunded">Mark as Refunded</option>
                                  <option value="chargeback">Mark as Chargeback</option>
                                </select>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setShowPaymentsModal(false);
                      setSelectedInvoice(null);
                      setPayments([]);
                      resetAddPaymentForm();
                      setTenantPaymentMethods([]);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Settlements Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    {importResult ? 'Import Results' : 'Import Settlements from CSV'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      resetImportForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {!importResult ? (
                  // Import Form
                  <form onSubmit={handleImportSubmit}>
                    <div className="space-y-4">
                      {importError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                          {importError}
                        </div>
                      )}

                      {/* Tenant Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tenant *
                        </label>
                        <select
                          required
                          value={importTenantId}
                          onChange={(e) => {
                            setImportTenantId(e.target.value);
                            setSelectedPaymentMethod('');
                            fetchPaymentMethodsForTenant(e.target.value);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="">Select a tenant</option>
                          {tenants.map((tenant) => (
                            <option key={tenant.tenant_id} value={tenant.tenant_id}>
                              {resolveOrganizationName(organizations, tenant.organization_id)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Payment Method Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Method *
                        </label>
                        <select
                          required
                          value={selectedPaymentMethod}
                          onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          disabled={!importTenantId}
                        >
                          <option value="">Select payment method</option>
                          {paymentMethods.map((pm) => (
                            <option key={pm.payment_method_id} value={pm.payment_method_id}>
                              {pm.display_name || pm.payment_method_name} (Prefix: {pm.settings?.reference_prefix})
                            </option>
                          ))}
                        </select>
                        {importTenantId && paymentMethods.length === 0 && (
                          <p className="mt-1 text-sm text-yellow-600">
                            No payment methods with a reference prefix configured for this tenant.
                            Please configure a reference prefix in the Payment Methods settings.
                          </p>
                        )}
                      </div>

                      {/* Bank Format Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bank Format *
                        </label>
                        <select
                          required
                          value={selectedBankFormat}
                          onChange={(e) => setSelectedBankFormat(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="">Select bank format</option>
                          {bankFormats.map((bf) => (
                            <option key={bf.id} value={bf.id}>
                              {bf.bankName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* File Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CSV File *
                        </label>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Upload a CSV file exported from your bank. The file should contain transaction data
                          with payment references in the Buchungstext/Verwendungszweck column.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowImportModal(false);
                          resetImportForm();
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-[var(--gold-pale)]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={importLoading || !importTenantId || !selectedPaymentMethod || !selectedBankFormat || !importFile}
                        className="px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)] disabled:opacity-50"
                      >
                        {importLoading ? 'Importing...' : 'Import'}
                      </button>
                    </div>
                  </form>
                ) : (
                  // Results View
                  <div>
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-900">{importResult.total_rows}</div>
                        <div className="text-sm text-gray-500">Total Rows</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">{importResult.successful_imports}</div>
                        <div className="text-sm text-green-600">Imported</div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-700">{importResult.failed_imports}</div>
                        <div className="text-sm text-red-600">Failed</div>
                      </div>
                    </div>

                    {/* Success message */}
                    {importResult.successful_imports > 0 && (
                      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                        Successfully imported {importResult.successful_imports} payment{importResult.successful_imports !== 1 ? 's' : ''}.
                      </div>
                    )}

                    {/* Failed Rows Table */}
                    {importResult.results.filter((r) => !r.success).length > 0 && (
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Failed Rows</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Row #</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Invoice</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {importResult.results
                                .filter((r) => !r.success)
                                .map((row) => (
                                  <tr key={row.row_number}>
                                    <td className="px-4 py-3 text-sm text-gray-900">{row.row_number}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{row.invoice_number || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {row.amount !== undefined ? formatCurrency(row.amount, 'EUR') : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-red-600">{row.error_reason}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => {
                          setShowImportModal(false);
                          resetImportForm();
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
